/**
 * Export utilities for CSV and Excel
 */

/**
 * Export data to CSV file
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects { key: 'field', label: 'Display Name' }
 * @param {String} filename - Name of the file to download
 */
export const exportToCSV = (data, headers, filename = "export.csv") => {
    if (!data || data.length === 0) {
        alert("No data to export");
        return;
    }

    // Create CSV header row
    const headerRow = headers.map((h) => `"${h.label}"`).join(",");

    // Create CSV data rows
    const dataRows = data.map((row) => {
        return headers
            .map((h) => {
                let value = row[h.key];
                // Handle nested objects
                if (value && typeof value === "object") {
                    value = value.name || value.code || JSON.stringify(value);
                }
                // Handle null/undefined
                if (value === null || value === undefined) {
                    value = "";
                }
                // Escape quotes and wrap in quotes
                value = String(value).replace(/"/g, '""');
                return `"${value}"`;
            })
            .join(",");
    });

    // Combine header and data
    const csvContent = [headerRow, ...dataRows].join("\n");

    // Add BOM for UTF-8 to ensure Excel opens it correctly
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });

    // Create download link
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Export data to Excel (XLSX) file
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects { key: 'field', label: 'Display Name' }
 * @param {String} filename - Name of the file to download
 */
export const exportToExcel = (data, headers, filename = "export.xlsx") => {
    if (!data || data.length === 0) {
        alert("No data to export");
        return;
    }

    // Dynamic import of xlsx library
    import("xlsx").then((XLSX) => {
        // Create worksheet data
        const worksheetData = [
            headers.map((h) => h.label), // Header row
            ...data.map((row) =>
                headers.map((h) => {
                    let value = row[h.key];
                    // Handle nested objects
                    if (value && typeof value === "object") {
                        value = value.name || value.code || JSON.stringify(value);
                    }
                    return value || "";
                })
            ),
        ];

        // Create workbook and worksheet
        const ws = XLSX.utils.aoa_to_sheet(worksheetData);

        // Auto-size columns
        const colWidths = headers.map((h, idx) => {
            const maxLength = Math.max(
                h.label.length,
                ...data.map((row) => {
                    let value = row[h.key];
                    if (value && typeof value === "object") {
                        value = value.name || value.code || "";
                    }
                    return String(value || "").length;
                })
            );
            return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
        });
        ws["!cols"] = colWidths;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

        // Write file
        XLSX.writeFile(wb, filename);
    });
};
